/**
 * Landing-page SVG illustrations are injected via innerHTML. The Chakra reset
 * (`* { font: inherit }`) ignores SVG presentation attributes, so labels fall
 * back to body size and overflow their chips.
 *
 * Promote typography attrs to inline styles. CSS px on SVG text is then scaled
 * by the viewBox CTM and reads heavier than mockup user-units — FONT_USER_SCALE
 * brings chip labels back inside their bounds.
 */

/** Shrink promoted font sizes so labels match mockup visual weight. */
export const LANDING_ILLUSTRATION_FONT_SCALE = 0.78

function scaleFontNumber(raw: string, scale: number): string {
  const n = Number.parseFloat(raw)
  if (!Number.isFinite(n)) {
    return raw
  }
  const scaled = n * scale
  return Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(2).replace(/\.?0+$/, '')
}

/**
 * Promote font presentation attributes on `<text>` / `<tspan>` to inline styles
 * and drop invalid `height="auto"`.
 */
export function prepareLandingIllustrationSvg(
  markup: string,
  scale: number = LANDING_ILLUSTRATION_FONT_SCALE
): string {
  let next = markup.replace(/\sviewbox=/gi, ' viewBox=').replace(/\sheight="auto"/gi, '')

  next = next.replace(/<(text|tspan)\b([^>]*)>/gi, (_full, tag: string, rawAttrs: string) => {
    let attrs = rawAttrs
    let existingStyle = ''
    attrs = attrs.replace(/\sstyle="([^"]*)"/i, (_m: string, style: string) => {
      existingStyle = style
      return ''
    })

    const styleParts: string[] = []

    const fontSizeMatch = attrs.match(/\sfont-size="([^"]*)"/i)
    if (fontSizeMatch) {
      styleParts.push(`font-size:${scaleFontNumber(fontSizeMatch[1], scale)}px`)
    }

    const fontWeightMatch = attrs.match(/\sfont-weight="([^"]*)"/i)
    if (fontWeightMatch) {
      styleParts.push(`font-weight:${fontWeightMatch[1]}`)
    }

    const fontFamilyMatch = attrs.match(/\sfont-family="([^"]*)"/i)
    if (fontFamilyMatch) {
      styleParts.push(`font-family:${fontFamilyMatch[1]}`)
    }

    const letterSpacingMatch = attrs.match(/\sletter-spacing="([^"]*)"/i)
    if (letterSpacingMatch) {
      styleParts.push(`letter-spacing:${scaleFontNumber(letterSpacingMatch[1], scale)}px`)
    }

    attrs = attrs
      .replace(/\sfont-size="[^"]*"/gi, '')
      .replace(/\sfont-weight="[^"]*"/gi, '')
      .replace(/\sfont-family="[^"]*"/gi, '')
      .replace(/\sletter-spacing="[^"]*"/gi, '')

    const style = [...styleParts, existingStyle].filter(Boolean).join(';')
    return `<${tag}${attrs}${style ? ` style="${style}"` : ''}>`
  })

  return next
}

/** Prefix gradient/filter IDs so several illustrations can share a page. */
export function prefixLandingIllustrationIds(svg: string, prefix: string): string {
  const ids = [...svg.matchAll(/\bid="([^"]+)"/g)].map(match => match[1])
  let result = svg
  for (const id of ids) {
    result = result.replaceAll(`id="${id}"`, `id="${prefix}${id}"`)
    result = result.replaceAll(`url(#${id})`, `url(#${prefix}${id})`)
  }
  return result
}
